import React, { useState } from 'react';
import './PrivacyPolicy.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, BackButton } from './shared';
import { useLogout, useUserData, useScrollToTop } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Issue #68: Privacy Policy Page (Datenschutzerklaerung) - Authenticated version
 * For logged-in users accessing from Profile page
 */
export function AuthenticatedPrivacyPolicy() {
  const navigation = useNavigation();
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { t } = useLanguage();
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  return (
    <PrivacyPolicyContent
      t={t}
      isAuthenticated={true}
      onNavigateBack={navigation.onNavigateBack}
      userData={userData}
      handleLogout={handleLogout}
      isLoggingOut={isLoggingOut}
      logoutError={logoutError}
      clearLogoutError={clearLogoutError}
      showCreateOptions={showCreateOptions}
      setShowCreateOptions={setShowCreateOptions}
    />
  );
}

/**
 * Issue #68: Privacy Policy Page (Datenschutzerklaerung) - Unauthenticated version
 * For users accessing from Register page before creating account
 */
function PrivacyPolicy({ onNavigateBack }) {
  const { t } = useLanguage();

  return (
    <PrivacyPolicyContent
      t={t}
      isAuthenticated={false}
      onNavigateBack={onNavigateBack}
    />
  );
}

/**
 * Shared content component for Privacy Policy
 */
function PrivacyPolicyContent({
  t,
  isAuthenticated,
  onNavigateBack,
  userData,
  handleLogout,
  isLoggingOut,
  logoutError,
  clearLogoutError,
  showCreateOptions,
  setShowCreateOptions
}) {
  // Issue #374: Always start the document from the top, regardless of the
  // scroll position of the previously-displayed screen.
  useScrollToTop();

  // Privacy policy content
  const privacyPolicyContent = `DATENSCHUTZERKLÄRUNG

Stand: 1. Juli 2026

================================================================================
1. VERANTWORTLICHER
================================================================================

Verantwortlich für die Datenverarbeitung auf dieser Plattform ist:

GAIA Software UG (haftungsbeschränkt)
Gerhardstraße 6
22926 Ahrensburg
Deutschland

E-Mail: datenschutz@universe-app.online

================================================================================
2. DATENSCHUTZBEAUFTRAGTER
================================================================================

Ein Datenschutzbeauftragter wurde nicht bestellt, da hierzu derzeit keine gesetzliche Verpflichtung besteht. Bei Fragen zum Datenschutz können Sie sich jederzeit an die oben genannte Kontaktadresse wenden.

================================================================================
3. ALLGEMEINE HINWEISE
================================================================================

3.1 Geltungsbereich

Diese Datenschutzerklärung gilt für die Nutzung der Plattform UNIVERSE
(nachfolgend "Plattform" oder "Dienst"), einem sozialen Netzwerk
ausschließlich für Studierende an deutschen Hochschulen.

3.2 Rechtsgrundlagen

Wir verarbeiten Ihre personenbezogenen Daten auf Grundlage der folgenden
Rechtsvorschriften:

- EU-Datenschutz-Grundverordnung (DSGVO)
- Bundesdatenschutzgesetz (BDSG)
- Telekommunikation-Digitale-Dienste-Datenschutz-Gesetz (TDDDG)

3.3 Grundsätze der Datenverarbeitung

Wir verarbeiten Ihre Daten nur:
- Wenn eine Rechtsgrundlage dies erlaubt (Art. 6 DSGVO)
- Für festgelegte, eindeutige und legitime Zwecke
- In dem Umfang, der für diese Zwecke erforderlich ist
- Unter Gewährleistung angemessener Sicherheit

================================================================================
4. ERHEBUNG UND VERARBEITUNG PERSONENBEZOGENER DATEN
================================================================================

4.1 Bei der Registrierung

Bei der Erstellung eines Benutzerkontos erheben wir folgende Daten:

| Datenfeld           | Zweck                                      | Rechtsgrundlage      |
|---------------------|--------------------------------------------|-----------------------|
| Vorname             | Identifikation, Personalisierung           | Art. 6(1)(b) DSGVO   |
| Nachname            | Identifikation                             | Art. 6(1)(b) DSGVO   |
| E-Mail-Adresse      | Authentifizierung, Verifizierung, Kontakt  | Art. 6(1)(b) DSGVO   |
| Passwort (gehasht)  | Kontosicherheit                            | Art. 6(1)(b) DSGVO   |
| Hochschule          | Community-Zuordnung, Inhaltsfilterung      | Art. 6(1)(b) DSGVO   |

Die Angabe dieser Daten ist für die Nutzung der Plattform erforderlich.
Ohne diese Daten kann kein Benutzerkonto erstellt werden.

Zusätzlich speichern wir den Zeitpunkt, zu dem Sie bei der Registrierung
unseren Nutzungsbedingungen (AGB) zugestimmt haben. Dies dient dem Nachweis
des Vertragsschlusses und der akzeptierten Vertragsbedingungen.
Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Dokumentation und Nachweisbarkeit des Vertragsschlusses).

Wir akzeptieren ausschließlich E-Mail-Adressen von deutschen Hochschulen,
um sicherzustellen, dass nur Studierende Zugang zur Plattform erhalten.

Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO.

4.2 Profilbild

Sie können optional ein Profilbild hochladen. Dieses wird auf unseren
Servern gespeichert und anderen Nutzern angezeigt.

- Maximale Dateigröße: 5 MB
- Erlaubte Formate: JPEG, PNG, WebP
- Speicherort: EU-Server
- Löschung: Bei Kontolöschung oder manueller Entfernung

Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO.

4.3 Nutzergenerierte Inhalte

Bei der Nutzung unserer Plattform können Sie folgende Inhalte erstellen:

a) Beiträge (Posts)
   - Textinhalte
   - Bilder (max. 10 pro Beitrag, je max. 5 MB)
   - Zeitstempel der Erstellung
   - Zugehörigkeit zur Hochschule

b) Kommentare
   - Textinhalte
   - Zeitstempel
   - Bezug zum kommentierten Beitrag

c) Marktplatz-Anzeigen
   - Titel und Beschreibung
   - Preis
   - Bilder
   - Angebotstyp (Suche/Biete)

d) Kursbewertungen
   - Sternebewertung (1-5)
   - Bewertungstext
   - Kurs- und Hochschulzuordnung

e) Chat-Nachrichten
   - Nachrichteninhalt
   - Zeitstempel
   - Empfänger

Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO.

4.4 Anonyme Beiträge

Sie haben die Möglichkeit, Beiträge und Kommentare anonym zu
veröffentlichen. In diesem Fall wird Ihr Name nicht öffentlich angezeigt.

Hinweis: Auch bei anonymen Beiträgen speichern wir intern eine Verknüpfung
zu Ihrem Benutzerkonto. Dies ist notwendig für:
- Inhaltsmoderation bei Verstößen gegen unsere Nutzungsbedingungen
- Erfüllung rechtlicher Anforderungen

4.5 Interaktionsdaten

Wir speichern Daten zu Ihren Interaktionen auf der Plattform:

| Aktion              | Gespeicherte Daten                         | Rechtsgrundlage      |
|---------------------|--------------------------------------------|-----------------------|
| Likes               | Beitrag-ID, Nutzer-ID, Zeitstempel         | Art. 6(1)(b) DSGVO   |
| Favoriten           | Marktplatz-Objekt-ID, Nutzer-ID            | Art. 6(1)(b) DSGVO   |
| Meldungen           | Gemeldeter Inhalt, Kategorie, Begründung   | Art. 6(1)(f) DSGVO   |
| Blockierungen       | Blockierender/Blockierter Nutzer-ID, Zeit  | Art. 6(1)(b) DSGVO   |

4.6 Nutzungsstatistiken

Zur Verbesserung der Plattform und zur Ermittlung von Nutzungskennzahlen
(z.B. Anzahl aktiver Nutzer pro Tag und Monat) speichern wir bestimmte
Aktivitäten, etwa das Aufrufen oder Erstellen von Beiträgen, Suchvorgänge
oder das Senden von Chat-Nachrichten. Erfasst werden dabei die Art der
Aktivität, der Zeitpunkt und Ihre Nutzer-ID.

Da diese Daten mit Ihrem Benutzerkonto verknüpft sind, handelt es sich um
personenbezogene (pseudonymisierte) Daten. Wir verwenden sie ausschließlich
intern und geben sie nicht an Dritte weiter. Für Statistiken werden sie zu
aggregierten, anonymen Kennzahlen zusammengefasst. Bei Löschung Ihres Kontos
werden die mit Ihnen verknüpften Aktivitätsdaten gelöscht; nur anonyme,
aggregierte Kennzahlen bleiben erhalten.

Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Verbesserung und Weiterentwicklung der Plattform).

4.7 Feedback und Bewertungen

Sie können uns freiwillig Feedback zur Plattform geben, insbesondere eine
Weiterempfehlungsbewertung (Skala 1-10) mit optionalem Freitext. Beta-
Testerinnen und -Tester können zusätzlich eine Gesamtbewertung abgeben.
Diese Angaben speichern wir zusammen mit Ihrer Nutzer-ID.

Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Qualitätsverbesserung).

4.8 Automatisch erfasste Daten

Bei der Nutzung der Plattform werden automatisch folgende Daten erfasst:

a) Session-Daten
   - Session-Token (zufällig generiert)
   - Erstellungszeitpunkt
   - Ablaufzeitpunkt (24 Stunden)
   - Letzte Aktivität

b) Protokolldaten (Server-Logs)
   - IP-Adresse
   - Zeitpunkt des Zugriffs
   - Aufgerufene Seiten
   - Browsertyp und -version
   - Betriebssystem

c) Fehlerprotokolle
   - Bei technischen Fehlern speichern wir Angaben zum Fehler, ggf. die
     betroffene Nutzer-ID, die IP-Adresse und den aufgerufenen Endpunkt.
   - Zweck: Fehleranalyse und Sicherstellung des technischen Betriebs.

Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Sicherheit, Missbrauchsprävention, technischer Betrieb).

================================================================================
5. COOKIES UND SESSION-MANAGEMENT
================================================================================

5.1 Technisch notwendige Cookies

Wir verwenden ausschließlich technisch notwendige Session-Cookies für
die Authentifizierung:

| Cookie-Name    | Zweck                          | Speicherdauer | Einwilligung erforderlich |
|----------------|--------------------------------|---------------|---------------------------|
| session_token  | Benutzerauthentifizierung      | 24 Stunden    | Nein (technisch notwendig)|

Diese Cookies sind für den Betrieb der Plattform zwingend erforderlich
und fallen unter die Ausnahme nach § 25 TDDDG für "unbedingt erforderliche"
Cookies. Eine Einwilligung ist daher nicht erforderlich.

5.2 Keine Tracking-Cookies

Wir verwenden KEINE:
- Analyse-Cookies (z.B. Google Analytics)
- Werbe-Cookies
- Social-Media-Tracking
- Drittanbieter-Cookies

================================================================================
6. E-MAIL-KOMMUNIKATION
================================================================================

Wir senden Ihnen folgende transaktionale E-Mails:

6.1 Verifizierungs-E-Mail

- Anlass: Nach der Registrierung
- Inhalt: Link zur Bestätigung Ihrer E-Mail-Adresse
- Gültigkeit: 24 Stunden
- Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO

6.2 Passwort-Zurücksetzung

- Anlass: Auf Anforderung bei "Passwort vergessen"
- Inhalt: 6-stelliger Einmal-Code (OTP)
- Gültigkeit: 24 Stunden
- Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO

6.3 Sicherheitsbenachrichtigung

- Anlass: Nach erfolgreicher Passwortänderung
- Inhalt: Hinweis auf Passwortänderung
- Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO

6.4 Benachrichtigung bei automatischer Inhaltslöschung

- Anlass: Wenn Inhalte aufgrund von Mehrfachmeldungen (5 oder mehr)
  automatisch entfernt werden
- Inhalt: Information über Löschung, Grund (gemeldete Kategorien),
  Hinweis auf verbleibende Verstöße bis zur Kontosperrung
- Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
  (Transparenz, Community-Schutz)

Diese E-Mails sind für die Nutzung der Plattform erforderlich und können
nicht abbestellt werden. Sie enthalten keine Werbung.

================================================================================
7. BENACHRICHTIGUNGEN
================================================================================

7.1 In-App-Benachrichtigungen

Wir informieren Sie in der Plattform über:
- Neue Kommentare zu Ihren Beiträgen
- Likes auf Ihre Beiträge
- Neue Chat-Nachrichten
- Verkauf von Marktplatz-Favoriten

7.2 Benachrichtigungseinstellungen

Sie können in Ihren Profileinstellungen festlegen, welche Benachrichtigungen
Sie erhalten möchten:
- Kommentar-Benachrichtigungen
- Like-Benachrichtigungen
- Favoriten-Benachrichtigungen
- Chat-Benachrichtigungen

Zusätzlich können Sie Benachrichtigungen für einzelne Chat-Konversationen
deaktivieren. Diese Einstellung wird pro Chat gespeichert.

Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO.

7.3 Push-Benachrichtigungen (optional)

Sofern Sie Push-Benachrichtigungen aktivieren, speichern wir die für den
Versand notwendigen technischen Daten:
- in der mobilen App: das Geräte-Token und die Plattform (Android/iOS)
- im Browser: die Abonnement-Daten des Browsers (Push-Adresse und
  zugehörige Schlüssel)

Rechtsgrundlage: Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO.
Sie können Push-Benachrichtigungen jederzeit in Ihren Geräte- bzw.
Browsereinstellungen deaktivieren.

================================================================================
8. WEITERGABE VON DATEN AN DRITTE
================================================================================

8.1 Auftragsverarbeiter

Wir arbeiten mit folgenden Dienstleistern zusammen:

a) E-Mail-Versand
   - Anbieter: Strato AG
   - Zweck: Versand von Transaktions-E-Mails
   - Übermittelte Daten: E-Mail-Adresse, Vorname
   - Standort: EU (Deutschland)
   - Grundlage: Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO

b) Server-Hosting
   - Anbieter: Strato AG
   - Zweck: Bereitstellung der Server-Infrastruktur
   - Standort: EU
   - Grundlage: Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO

8.2 Keine Weitergabe an Dritte

Wir verkaufen, vermieten oder teilen Ihre personenbezogenen Daten NICHT
mit Dritten zu Marketing- oder Werbezwecken.

8.3 Gesetzliche Offenlegungspflichten

Wir können Daten offenlegen, wenn wir gesetzlich dazu verpflichtet sind,
insbesondere auf Anordnung von:
- Gerichten
- Strafverfolgungsbehörden
- Datenschutzaufsichtsbehörden

Rechtsgrundlage: Rechtliche Verpflichtung gemäß Art. 6 Abs. 1 lit. c DSGVO.

================================================================================
9. DATENÜBERMITTLUNG IN DRITTLÄNDER
================================================================================

Eine Übermittlung Ihrer personenbezogenen Daten in Länder außerhalb der
Europäischen Union (EU) oder des Europäischen Wirtschaftsraums (EWR)
findet nicht statt.

================================================================================
10. SPEICHERDAUER
================================================================================

Wir speichern Ihre Daten nur so lange, wie es für die jeweiligen Zwecke
erforderlich ist:

| Datenkategorie              | Speicherdauer                                           |
|-----------------------------|---------------------------------------------------------|
| Kontodaten                  | Bis zur Kontolöschung + 30 Tage Wiederherstellungsfrist |
| Session-Tokens              | 24 Stunden                                              |
| Verifizierungs-Tokens       | 24 Stunden                                              |
| Passwort-Reset-Tokens       | 24 Stunden (oder nach Verwendung)                       |
| Blockierungsdaten           | Bis zur Aufhebung oder Kontolöschung                    |
| Beiträge und Kommentare     | Bis zur Löschung durch Nutzer                           |
| Marktplatz-Anzeigen         | Bis zur Löschung durch Nutzer                           |
| Chat-Nachrichten            | Bis zur Löschung des Gesprächs                          |
| Kursbewertungen             | Bis zur Löschung durch Nutzer                           |
| Profilbilder                | Bis zur Entfernung oder Kontolöschung                   |
| Nutzungsstatistiken         | Bis zur Kontolöschung; danach nur noch anonym/aggregiert|
| Feedback und Bewertungen    | Bis zur Kontolöschung                                   |
| Push-Abonnements            | Bis zur Abmeldung oder Kontolöschung                    |
| Server-Logs                 | 30 Tage                                                 |
| Fehlerprotokolle            | 30 Tage                                                 |
| Backup-Daten                | 90 Tage                                                 |

Bei Kontolöschung werden alle mit Ihrem Konto verknüpften Daten gelöscht,
sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.

10.1 Wiederherstellungsfrist bei Kontolöschung

Wenn Sie die Löschung Ihres Kontos beantragen, beginnt eine 30-tägige
Wiederherstellungsfrist:

- Während dieser 30 Tage können Sie Ihr Konto durch erneuten Login
  vollständig wiederherstellen
- Ihre Daten bleiben während dieser Zeit gespeichert, sind aber nicht
  aktiv (kein Zugriff durch andere Nutzer)
- Nach Ablauf der 30 Tage werden personenbezogene Daten sowie Ihre
  Beiträge, Kommentare, Marktplatzangebote, Chat-Nachrichten und
  hochgeladenen Bilder unwiderruflich gelöscht
- Kursbewertungen werden nicht gelöscht, sondern anonymisiert (Ihr Name
  wird entfernt; die Bewertung bleibt als "Anonym" erhalten)

Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Schutz vor versehentlicher Löschung, Wiederherstellungsmöglichkeit).

================================================================================
11. IHRE RECHTE ALS BETROFFENE PERSON
================================================================================

Sie haben gegenüber uns folgende Rechte bezüglich Ihrer personenbezogenen
Daten:

11.1 Auskunftsrecht (Art. 15 DSGVO)

Sie haben das Recht, eine Bestätigung darüber zu verlangen, ob wir
personenbezogene Daten von Ihnen verarbeiten. Ist dies der Fall, haben
Sie ein Recht auf Auskunft über diese Daten sowie weitere Informationen
gemäß Art. 15 DSGVO.

11.2 Recht auf Berichtigung (Art. 16 DSGVO)

Sie haben das Recht, unverzüglich die Berichtigung unrichtiger oder
Vervollständigung unvollständiger personenbezogener Daten zu verlangen.

11.3 Recht auf Löschung (Art. 17 DSGVO)

Sie haben das Recht, die Löschung Ihrer personenbezogenen Daten zu
verlangen, sofern nicht die Verarbeitung zur Erfüllung einer rechtlichen
Verpflichtung erforderlich ist.

Um Ihr Konto und alle damit verbundenen Daten zu löschen, nutzen Sie
bitte die Funktion "Konto löschen" in Ihren Profileinstellungen oder
kontaktieren Sie uns per E-Mail. Was bei der Löschung mit Ihren Inhalten
geschieht, ist in Abschnitt 10.1 beschrieben.

11.4 Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)

Sie haben das Recht, die Einschränkung der Verarbeitung zu verlangen,
insbesondere wenn:
- Sie die Richtigkeit der Daten bestreiten
- Die Verarbeitung unrechtmäßig ist
- Wir die Daten nicht mehr benötigen

11.5 Recht auf Datenübertragbarkeit (Art. 20 DSGVO)

Sie haben das Recht, Ihre personenbezogenen Daten in einem strukturierten,
gängigen und maschinenlesbaren Format zu erhalten. Nutzen Sie hierfür
die Funktion "Daten exportieren" in Ihren Profileinstellungen.

11.6 Widerspruchsrecht (Art. 21 DSGVO)

Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation
ergeben, jederzeit gegen die Verarbeitung Sie betreffender personenbezogener
Daten, die aufgrund von Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)
erfolgt, Widerspruch einzulegen.

11.7 Recht auf Widerruf der Einwilligung (Art. 7 Abs. 3 DSGVO)

Soweit die Verarbeitung auf Ihrer Einwilligung beruht (z.B. Push-
Benachrichtigungen), haben Sie das Recht, diese jederzeit zu widerrufen.
Der Widerruf berührt nicht die Rechtmäßigkeit der bis dahin erfolgten
Verarbeitung.

11.8 Beschwerderecht bei einer Aufsichtsbehörde (Art. 77 DSGVO)

Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über
die Verarbeitung Ihrer personenbezogenen Daten zu beschweren.

================================================================================
12. AUFSICHTSBEHÖRDE
================================================================================

Zuständige Aufsichtsbehörde:

Unabhängiges Landeszentrum für Datenschutz Schleswig-Holstein (ULD)
Holstenstraße 98
24103 Kiel
E-Mail: mail@datenschutzzentrum.de
Telefon: 0431 988-1200
Website: datenschutzzentrum.de

================================================================================
13. DATENSICHERHEIT
================================================================================

Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, um
Ihre Daten gegen zufällige oder vorsätzliche Manipulationen, Verlust,
Zerstörung oder den Zugriff unberechtigter Personen zu schützen:

13.1 Technische Maßnahmen

- Verschlüsselte Datenübertragung nach dem Stand der Technik (TLS/HTTPS)
- Sichere Speicherung von Passwörtern mittels anerkannter Hash-Verfahren
  (Passwörter werden zu keinem Zeitpunkt im Klartext gespeichert)
- Geschützte, zeitlich begrenzte Anmeldesitzungen
- Schutz vor gängigen Angriffen (z.B. auf Datenbank und Dateiuploads)
- Zugangsbeschränkung auf die Registrierung mit Hochschul-E-Mail-Adressen

13.2 Organisatorische Maßnahmen

- Beschränkter Zugang zu personenbezogenen Daten
- Regelmäßige Sicherheitsüberprüfungen
- Dokumentierte Datenschutzprozesse

================================================================================
14. INHALTSMODERATION
================================================================================

Zum Schutz unserer Nutzer-Community führen wir eine Inhaltsmoderation
durch:

14.1 Meldungen durch Nutzer

Nutzer können unangemessene Inhalte melden. Wir speichern dabei:
- Gemeldeten Inhalt
- Meldekategorie (z.B. Hassrede, Spam, Bedrohung)
- Optionale Begründung

14.2 Automatische Maßnahmen

- Beiträge mit mehreren Meldungen werden überprüft
- Bei schweren oder wiederholten Verstößen kann eine Kontosperrung erfolgen

14.2a Wortfilter und automatische Inhaltserkennung

Vor dem Hochladen von Inhalten führen wir eine automatische Prüfung auf
unangemessene Wörter oder Ausdrücke durch:

- Verarbeitung: Der Inhalt wird lokal geprüft, bevor er auf dem Server
  gespeichert wird
- Bei Erkennung unangemessener Wörter: Der Upload wird blockiert und
  der Nutzer wird informiert, welche Wörter den Upload verhindern
- Es werden keine detaillierten Logs über blockierte Inhalte gespeichert
- Die Liste der gefilterten Wörter wird nicht veröffentlicht

14.2b Automatische Löschung bei Mehrfachmeldungen

Inhalte, die von 5 oder mehr verschiedenen Nutzern gemeldet werden,
werden automatisch entfernt:

- Der Autor wird per E-Mail über die Löschung informiert
- Die Meldekategorien werden in der E-Mail genannt
- Der Autor kann Widerspruch beim Kundenservice einlegen
- Wiederherstellung ist durch Administratoren möglich
- Wird ein Inhalt wiederhergestellt, erfolgt eine erneute automatische
  Löschung erst nach 5 weiteren Meldungen

Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Schutz der Community, Plattformsicherheit).

================================================================================
15. ÄNDERUNGEN DIESER DATENSCHUTZERKLÄRUNG
================================================================================

Wir behalten uns vor, diese Datenschutzerklärung anzupassen, um sie an
geänderte Rechtslagen oder bei Änderungen des Dienstes oder der
Datenverarbeitung anzupassen.

Die aktuelle Version finden Sie stets auf unserer Plattform. Bei
wesentlichen Änderungen werden wir Sie per E-Mail oder In-App-
Benachrichtigung informieren.

Das Datum der letzten Aktualisierung finden Sie am Anfang dieser
Datenschutzerklärung.

================================================================================
16. KONTAKT
================================================================================

Bei Fragen zur Verarbeitung Ihrer personenbezogenen Daten, zur Ausübung
Ihrer Rechte oder zu dieser Datenschutzerklärung wenden Sie sich bitte an:

datenschutz@universe-app.online

================================================================================

Stand: 1. Juli 2026

================================================================================`;

  return (
    <div className="privacy-policy-page">
      {/* Header - Full AppHeader for authenticated, simple header for unauthenticated */}
      {isAuthenticated ? (
        <AppHeader
          userData={userData}
          onLogout={handleLogout}
          isLoggingOut={isLoggingOut}
          logoutError={logoutError}
          onClearLogoutError={clearLogoutError}
        />
      ) : (
        <header className="privacy-simple-header">
          <h1 className="privacy-header-title">{t('profile.privacyPolicy')}</h1>
        </header>
      )}

      {/* Tab Navigation - Only for authenticated users */}
      {isAuthenticated && (
        <div className="tab-navigation-privacy">
          <span className="tab-title">{t('profile.privacyPolicy')}</span>
        </div>
      )}

      {/* Privacy Policy Content */}
      <main className={`privacy-policy-content ${!isAuthenticated ? 'unauthenticated' : ''}`}>
        {/* Back Button */}
        <BackButton
          onClick={onNavigateBack}
          className="privacy-back-button"
        />

        {/* Privacy Policy Text */}
        <div className="privacy-policy-text">
          <pre>{privacyPolicyContent}</pre>
        </div>
      </main>

      {/* Bottom Navigation - Only for authenticated users */}
      {isAuthenticated && (
        <>
          <BottomNavigation
            activeTab="profile"
            onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
          />
          <CreateOptionsMenu
            isOpen={showCreateOptions}
            onClose={() => setShowCreateOptions(false)}
          />
        </>
      )}
    </div>
  );
}

export default PrivacyPolicy;
